import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, ArrowRight } from "lucide-react";
import AuthShell from "@/platform/components/AuthShell";
import { Button } from "@/platform/components/primitives";
import { api } from "@/platform/api";
import { useAuth } from "@/platform/AuthContext";
import { setPendingInvite, clearPendingInvite } from "@/platform/pendingInvite";

const ROLE_LABEL = { admin: "Admin", member: "Member" };

export default function JoinWorkspace() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading, refresh, logout } = useAuth();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => { setPendingInvite(token); }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.get(`/api/workspace/invites/accept/${token}`);
        setPreview(p);
      } catch (e) {
        setError(e.message || "This invitation link is invalid or has expired.");
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true); setError("");
    try {
      await api.post(`/api/workspace/invites/accept/${token}`);
      clearPendingInvite();
      await refresh();
      navigate("/app", { replace: true });
    } catch (e) {
      setError(e.message || "Couldn't accept the invitation.");
      setAccepting(false);
    }
  };

  const switchAccount = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <AuthShell>
      <div className="animate-in fade-in duration-500" data-testid="join-page">
        <div className="mb-5 grid h-12 w-12 place-items-center rounded-2xl" style={{ background: "rgba(124,107,255,0.18)" }}>
          <Users className="h-6 w-6" style={{ color: "#A99BFF" }} />
        </div>
        {error && !preview ? (
          <>
            <h1 className="text-[24px] font-semibold tracking-tight text-[#F4F5F8]">Invitation unavailable</h1>
            <p className="mt-2 text-[15px] text-[#98A0B0]" data-testid="join-error">{error}</p>
            <div className="mt-6"><Button onClick={() => navigate("/app")}>Go to Pinlayr</Button></div>
          </>
        ) : preview ? (
          <>
            <h1 className="text-[24px] font-semibold tracking-tight text-[#F4F5F8]">
              Join {preview.workspaceName}
            </h1>
            <p className="mt-2 text-[15px] text-[#98A0B0]">
              You've been invited to join <span className="text-[#E6E8EE]">{preview.workspaceName}</span> as{" "}
              <span className="text-[#E6E8EE]">{ROLE_LABEL[preview.role] || "Member"}</span>.
            </p>
            <p className="mt-1 text-[13px] text-[#6E7686]">Invitation sent to {preview.email}</p>

            {error && <p className="mt-4 text-[13.5px] text-[#F87171]" data-testid="join-accept-error">{error}</p>}

            {loading ? null : user ? (
              <div className="mt-7 space-y-3">
                <Button onClick={accept} loading={accepting} data-testid="join-accept-btn">
                  Accept &amp; join <ArrowRight className="h-4 w-4" />
                </Button>
                <button onClick={switchAccount} className="text-[13px] text-[#6E7686] hover:text-[#A99BFF]" data-testid="join-switch-account">
                  Signed in as {user.email} — use a different account
                </button>
              </div>
            ) : (
              <div className="mt-7 space-y-3">
                <Button onClick={() => navigate("/signup")} data-testid="join-create-account">Create an account to join</Button>
                <button onClick={() => navigate("/login")} className="text-[13px] font-medium text-[#A99BFF] hover:underline" data-testid="join-signin">
                  Already have a Pinlayr account? Sign in
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="grid place-items-center py-16"><span className="h-6 w-6 animate-spin rounded-full border-2 border-[#6D5EF6] border-t-transparent" /></div>
        )}
      </div>
    </AuthShell>
  );
}
